import React, {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { joinUrl } from "../utils/url";
import {
    GATEWAY_BASE,
    LOGIN_PATH,
    LOGOUT_PATH,
    OAUTH_TOKEN_PATH,
    OAUTH_CALLBACK_BASE_PATH,
    OAUTH,
    SANDBOX_AUTOLOGIN,
    SANDBOX_EMAIL,
    SANDBOX_PASSWORD,
} from "../config/env";
import { generatePKCE } from "../utils/pkce";

export type Provider = "GOOGLE" | "MICROSOFT";

type RotationBody = { expires_in?: number; expiresIn?: number; scope?: string };

interface AuthContextType {
    isAuthenticated: boolean;
    signInLocal(email: string, password: string): Promise<boolean>;
    signInSocial(provider: Provider): Promise<void>;
    refreshNow: () => Promise<boolean>;
    primeFromAuth: (accessExpiresInSec?: number) => void;
    logout: () => void;
    fetchWithAuth: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

export const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    signInLocal: async () => false,
    signInSocial: async () => {},
    refreshNow: async () => false,
    primeFromAuth: () => {},
    logout: () => {},
    fetchWithAuth: (i, init) => fetch(i, init),
});

const TOKEN_ENDPOINT = joinUrl(GATEWAY_BASE, OAUTH_TOKEN_PATH);
const LOGIN_ENDPOINT = joinUrl(GATEWAY_BASE, LOGIN_PATH);
const LOGOUT_ENDPOINT = joinUrl(GATEWAY_BASE, LOGOUT_PATH);

const SKEW_SEC = 60;
const DEFAULT_PERIOD_SEC = 15 * 60;

const SESSION_FLAG = "__rd_has_session__";
const hasSession = () => !!localStorage.getItem(SESSION_FLAG);
const markSession = () => localStorage.setItem(SESSION_FLAG, "1");
const clearSession = () => localStorage.removeItem(SESSION_FLAG);

declare global {
    interface Window {
        __rd_boot_refreshed__?: boolean;
        __rdRefreshInFlight?: Promise<boolean> | null;
        __rd_fetchWithAuth__?: (i: RequestInfo, init?: RequestInit) => Promise<Response>;
        __rd_wait_for_refresh__?: () => Promise<void>;
        __rd_call_refresh__?: () => Promise<boolean>;
    }
}

const normalizeSeconds = (v?: number) => {
    if (!v || Number.isNaN(v)) return undefined;
    return v > 86400 * 10 ? Math.floor(v / 1000) : v;
};

const parseExpiresFromJson = (json: { expires_in?: number; expiresIn?: number } | undefined) =>
    normalizeSeconds(json?.expires_in ?? json?.expiresIn);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setAuth] = useState<boolean>(() => hasSession());

    const refreshTimer = useRef<number | null>(null);
    const lastRefreshAt = useRef<number>(0);
    const refreshFnRef = useRef<() => Promise<boolean>>(() => Promise.resolve(false));

    const clearTimer = () => {
        if (refreshTimer.current !== null) {
            window.clearTimeout(refreshTimer.current);
            refreshTimer.current = null;
        }
    };

    const scheduleNext = useCallback((accessTtlSec?: number) => {
        clearTimer();
        const ttl = Math.max(30, Math.min(DEFAULT_PERIOD_SEC, (accessTtlSec ?? DEFAULT_PERIOD_SEC) - SKEW_SEC));
        refreshTimer.current = window.setTimeout(() => {
            if (hasSession()) {
                refreshFnRef.current().catch(() => {});
            }
        }, ttl * 1000);
    }, []);

    const performRefresh = useCallback(async (): Promise<boolean> => {
        const form = new URLSearchParams({ grant_type: "refresh_token" });
        const resp = await fetch(TOKEN_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            credentials: "include",
            body: form.toString(),
        });
        if (!resp.ok) return false;

        let accessTtl: number | undefined;
        try {
            const body = (await resp.json()) as RotationBody;
            accessTtl = parseExpiresFromJson(body);
        } catch {
            accessTtl = undefined;
        }

        scheduleNext(accessTtl);
        lastRefreshAt.current = Date.now();
        return true;
    }, [scheduleNext]);

    const refreshNow = useCallback(async (): Promise<boolean> => {
        if (!window.__rdRefreshInFlight) {
            window.__rdRefreshInFlight = performRefresh()
                .then((ok) => {
                    setAuth(ok);
                    if (ok) markSession();
                    else clearSession();
                    return ok;
                })
                .catch(() => {
                    setAuth(false);
                    clearSession();
                    return false;
                })
                .finally(() => {
                    window.__rdRefreshInFlight = null;
                });
        }
        return window.__rdRefreshInFlight!;
    }, [performRefresh]);

    const waitForRefreshIfRunning = useCallback(async () => {
        const inFlight = window.__rdRefreshInFlight;
        if (inFlight) {
            try {
                await inFlight;
            } catch {
                /* ignore; caller will handle auth state */
            }
        }
    }, []);

    const primeFromAuth = useCallback((accessExpiresInSec?: number) => {
        window.__rd_boot_refreshed__ = true;
        setAuth(true);
        markSession();
        lastRefreshAt.current = Date.now();
        scheduleNext(normalizeSeconds(accessExpiresInSec));
    }, [scheduleNext]);

    const signInLocal = useCallback(
        async (email: string, password: string) => {
            try {
                const resp = await fetch(LOGIN_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ email, password }),
                });
                if (!resp.ok) return false;

                let ttl: number | undefined;
                try {
                    const json = (await resp.json()) as { expires_in?: number; expiresIn?: number };
                    ttl = parseExpiresFromJson(json);
                } catch {
                    ttl = undefined;
                }

                primeFromAuth(ttl);
                return true;
            } catch {
                return false;
            }
        },
        [primeFromAuth]
    );

    const signInSocial = useCallback(async (provider: Provider) => {
        const cfg = OAUTH[provider];
        const { verifier, challenge } = await generatePKCE();
        sessionStorage.setItem("pkce_verifier", verifier);
        const state = Math.random().toString(36).slice(2);
        sessionStorage.setItem("oauth_state", state);

        const redirectUri = `${window.location.origin}${OAUTH_CALLBACK_BASE_PATH}/${provider.toLowerCase()}`;
        const params = new URLSearchParams({
            response_type: "code",
            client_id: cfg.clientId,
            redirect_uri: redirectUri,
            scope: cfg.scope,
            state,
            code_challenge: challenge,
            code_challenge_method: "S256",
        });

        window.location.href = `${cfg.authUrl}?${params}`;
    }, []);

    const logout = useCallback(() => {
        (async () => {
            try {
                await fetch(LOGOUT_ENDPOINT, { method: "POST", credentials: "include" });
            } catch {}
        })();

        clearTimer();
        clearSession();
        setAuth(false);
        window.__rdRefreshInFlight = null;
        lastRefreshAt.current = Date.now();

        if (SANDBOX_AUTOLOGIN && SANDBOX_EMAIL && SANDBOX_PASSWORD) {
            setTimeout(() => {
                void signInLocal(SANDBOX_EMAIL, SANDBOX_PASSWORD);
            }, 0);
        }
    }, [signInLocal]);

    // Boot: try a silent refresh once (even if localStorage flag isn't set yet)
    useEffect(() => {
        const isOnCallback = window.location.pathname.startsWith(OAUTH_CALLBACK_BASE_PATH);
        if (isOnCallback) return;
        if (window.__rd_boot_refreshed__) return;

        window.__rd_boot_refreshed__ = true;
        void refreshNow();
    }, [refreshNow]);

    // Keep session fresh on visibility/online changes
    useEffect(() => {
        lastRefreshAt.current = Date.now();

        const STALE_MS = 10 * 60 * 1000;
        const kickIfStale = () => {
            if (!hasSession()) return;
            if (Date.now() - lastRefreshAt.current > STALE_MS) void refreshNow();
        };

        const onVisible = () => {
            if (document.visibilityState === "visible") kickIfStale();
        };
        const onOnline = () => kickIfStale();

        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("online", onOnline);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("online", onOnline);
        };
    }, [refreshNow]);

    const fetchWithAuth = useCallback(
        async (input: RequestInfo, init?: RequestInit) => {
            const withCreds: RequestInit = { credentials: "include", ...init };

            // If a refresh is currently running (e.g., boot), wait so we don't fire a doomed request.
            await waitForRefreshIfRunning();

            let r = await fetch(input, withCreds);
            if (r.status !== 401) return r;

            const ok = await refreshNow();
            if (!ok) return r;

            return fetch(input, withCreds);
        },
        [refreshNow, waitForRefreshIfRunning]
    );

    // Publish helpers for non-React code (axios client, etc.)
    useEffect(() => {
        (window as any).__rd_fetchWithAuth__ = fetchWithAuth;
        (window as any).__rd_wait_for_refresh__ = waitForRefreshIfRunning;
        (window as any).__rd_call_refresh__ = refreshNow;
        return () => {
            if ((window as any).__rd_fetchWithAuth__ === fetchWithAuth) delete (window as any).__rd_fetchWithAuth__;
            if ((window as any).__rd_wait_for_refresh__ === waitForRefreshIfRunning) delete (window as any).__rd_wait_for_refresh__;
            if ((window as any).__rd_call_refresh__ === refreshNow) delete (window as any).__rd_call_refresh__;
        };
    }, [fetchWithAuth, waitForRefreshIfRunning, refreshNow]);

    const value = useMemo<AuthContextType>(
        () => ({
            isAuthenticated,
            signInLocal,
            signInSocial,
            refreshNow,
            primeFromAuth,
            logout,
            fetchWithAuth,
        }),
        [isAuthenticated, signInLocal, signInSocial, refreshNow, primeFromAuth, logout, fetchWithAuth]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
